import {
    ContractWarning,
    CustomerEventWarning,
    DayOffWarning,
    DocumentRetrievalWarning,
    LeadWarning,
    POCServiceWarning,
    ServiceMissingUUIDWarning,
    ServiceWaitingConfirmWarning,
    SubcontractDocumentRetrievalWarning,
    TicketWarning,
    WarningItem,
} from '@/modules/Util/components/Alerts/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { buildAlertMetadata } from './utils'

interface AlertDetailProps {
    alert: WarningItem
    index: number
}

const AlertDetail: React.FC<AlertDetailProps> = ({ alert, index }) => {
    const { t } = useTranslation()

    const getNavigationPath = (alert: WarningItem): string => {
        switch (alert.type) {
            case 'contract_expired':
            case 'contract_expiring': {
                const contractData = alert.data as ContractWarning
                return generatePathDatacentral(paths.contractDetail, {
                    id: contractData?.contract_id,
                })
            }
            case 'dayoff_pending': {
                const dayOffData = alert.data as DayOffWarning
                return `/dayoffs/${dayOffData?.day_off_id}`
            }
            case 'document_retrieval_upcoming':
            case 'document_retrieval_overdue': {
                const documentRetrievalData =
                    alert.data as DocumentRetrievalWarning
                return generatePathDatacentral(paths.contractDetail, {
                    id: documentRetrievalData?.contract_id,
                })
            }
            case 'subcontract_document_retrieval_overdue':
            case 'subcontract_document_retrieval_upcoming': {
                const subcontractDocumentRetrievalData =
                    alert.data as SubcontractDocumentRetrievalWarning
                return generatePathDatacentral(paths.contractDetail, {
                    id: subcontractDocumentRetrievalData?.contract_id,
                })
            }
            case 'poc_service_overdue':
            case 'poc_service_upcoming': {
                const pocServiceData = alert.data as POCServiceWarning
                return (
                    paths.services +
                    `?tab=service-poc&filters=${btoa(
                        JSON.stringify({
                            product_group_name: [
                                pocServiceData?.product_group_name,
                            ],
                            service_status: [pocServiceData?.service_status],
                        })
                    )}`
                )
            }
            case 'service_waiting_confirm': {
                const serviceWaitingConfirmData =
                    alert.data as ServiceWaitingConfirmWarning
                return (
                    paths.services +
                    `?tab=waiting-confirm&filters=${btoa(
                        JSON.stringify({
                            product_group_name: [
                                serviceWaitingConfirmData?.product_group_name,
                            ],
                        })
                    )}`
                )
            }
            case 'service_missing_uuid': {
                const serviceMissingUUIDData =
                    alert.data as ServiceMissingUUIDWarning
                return generatePathDatacentral(paths.serviceDetail, {
                    id: serviceMissingUUIDData?.service_id,
                })
            }
            case 'lead_unassigned': {
                // Mở thẳng chi tiết lead (modal overlay trên list) qua query
                // param contact_source_id — giống hành vi click 1 dòng trong
                // danh sách và giống mục lead_stale.
                const leadData = alert.data as LeadWarning
                return `${paths.customerMkt}?contact_source_id=${leadData?.contact_source_id}`
            }
            case 'lead_stale': {
                // Dùng query param contact_source_id để mở modal chi tiết (overlay
                // trên list) — đồng nhất với lead_unassigned; tránh route trang
                // riêng /contacts/{id} có giao diện khác.
                const leadData = alert.data as LeadWarning
                return `${paths.customerMkt}?contact_source_id=${leadData?.contact_source_id}`
            }
            case 'ticket_escalated': {
                const ticketData = alert.data as TicketWarning
                return generatePathDatacentral(paths.ticketDetail, {
                    id: ticketData?.ticket_id,
                })
            }
            case 'customer_event_upcoming': {
                const eventData = alert.data as CustomerEventWarning
                const month = eventData.event_month
                const safeMonth =
                    Number.isInteger(month) && month >= 1 && month <= 12
                        ? month
                        : new Date().getMonth() + 1
                return `${paths.accountCustomerEvents}?month=${safeMonth}`
            }
            default:
                return '#'
        }
    }

    // Build alert metadata
    const alertMetadata = buildAlertMetadata(alert, t)

    const renderAlertContent = () => {
        return (
            <div className="space-y-2">
                <div className="flex items-start gap-3">
                    <div className="flex-1">
                        <div className="flex items-start justify-between">
                            <div className="">
                                {/* Label */}
                                <Typogaphy className="text-primary text-sm font-medium">
                                    {alertMetadata.label}
                                </Typogaphy>

                                {/* Subtitle */}
                                <Typogaphy className="text-xs leading-relaxed font-medium">
                                    {alertMetadata.subtitle}
                                </Typogaphy>

                                {/* Description */}
                                <Typogaphy className="text-xs text-gray-500 leading-relaxed">
                                    {alertMetadata.description}
                                </Typogaphy>
                            </div>

                            <div className="flex flex-col items-end gap-2 ml-4">
                                <Chip
                                    size="small"
                                    color={
                                        alert.severity === 'high'
                                            ? 'error'
                                            : alert.severity === 'medium'
                                              ? 'warning'
                                              : 'info'
                                    }
                                    label={t(
                                        `alerts.severity.${alert.severity}`
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="group"
        >
            <Link
                to={getNavigationPath(alert)}
                className={`block p-2 border-b border-gray-100 hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-all duration-200 group-last:border-b-0 ${alert.severity === 'high' ? 'bg-red-50 hover:bg-red-100' : alert.severity === 'medium' ? 'bg-yellow-50 hover:bg-yellow-100' : 'bg-blue-50 hover:bg-blue-100'}`}
            >
                <div className="transform group-hover:translate-x-1 transition-transform duration-200">
                    {renderAlertContent()}
                </div>
            </Link>
        </motion.div>
    )
}

export default AlertDetail
